import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCartStore, { MAX_QUANTITY } from "../store/cartStore";
import useStatusStore from "../store/statusStore";
import { PageHead, VegetarianMark } from "../components/site/Blocks";
import { euro, Notice, QuantityStepper, SideDishSelect } from "../components/order/orderUi";
import { getSeason } from "../config/hours";
import { CONTACT } from "../config/location";

const Bestellen = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [sides, setSides] = useState({});

    const {
        deliveryAvailable,
        orderAvailable,
        fetchDeliveryAvailable,
        fetchOrderAvailable,
        fetchIsInDeliveryTime,
        fetchInPickupTime,
    } = useStatusStore();

    const cart = useCartStore((state) => state.cart);
    const { addItem, increaseQuantity, decreaseQuantity } = useCartStore();
    const total = useCartStore((state) => state.totalPrice());
    const itemCount = useCartStore((state) => state.totalItems());

    useEffect(() => {
        fetchDeliveryAvailable();
        fetchOrderAvailable();
        fetchIsInDeliveryTime();
        fetchInPickupTime();
        fetch(`/api/public/menus/name/Bestellkarte`)
            .then((response) => response.json())
            .then((data) => {
                setCategories(data.categories || []);
                setItems(data.items || []);
            })
            .catch(() => {});
    }, [fetchDeliveryAvailable, fetchOrderAvailable, fetchIsInDeliveryTime, fetchInPickupTime]);

    const countFor = (id) => cart.filter((c) => c.id === id).reduce((sum, c) => sum + c.quantity, 0);

    const add = (item) => {
        const beilage = item.sideDish ? sides[item._id] || "Pommes" : "";
        addItem(item._id, item.title, item.prices[0].amount, item.description, beilage);
    };

    const jump = (id) => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 170 });
    };

    const groups = categories
        .map((category) => ({
            ...category,
            items: items.filter((item) => item.category === category._id),
        }))
        .filter((group) => group.items.length > 0);

    const season = getSeason();

    return (
        <>
            <PageHead
                eyebrow="Bestellkarte"
                title="Bestellen"
                lead={`Zum Abholen während der Öffnungszeiten. Lieferung ${season.deliveryDays} 17–21 Uhr, sonntags 16–20 Uhr, ab 30 € Bestellwert im Umkreis von 9 km. Bezahlung vor Ort.`}
                className="!pb-10"
            />

            {!orderAvailable && (
                <div className="sec mb-10">
                    <Notice>
                        Bestellungen sind derzeit nicht möglich. Sie erreichen uns telefonisch unter{" "}
                        <a href={CONTACT.phoneHref} className="!text-messing-hell no-underline">
                            {CONTACT.phone}
                        </a>
                        .
                    </Notice>
                </div>
            )}
            {orderAvailable && !deliveryAvailable && (
                <div className="sec mb-6">
                    <Notice tone="light">
                        Lieferung ist zurzeit wegen hoher Auslastung nicht verfügbar. Abholung ist weiterhin möglich.
                    </Notice>
                </div>
            )}

            {/* Ohne Bestellmöglichkeit bleibt die Karte zum Ansehen da, nur ohne Warenkorb. */}
            {groups.length > 0 && (
                <div className="relative">
                    <nav
                        aria-label="Kategorien"
                        className="sec below-header z-10 flex gap-7 overflow-x-auto border-y border-forest/10 bg-creme [scrollbar-width:none]"
                    >
                        {groups.map((group) => (
                            <a
                                key={group._id}
                                href={`#kat-${group._id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    jump(`kat-${group._id}`);
                                }}
                                className="flex-none whitespace-nowrap py-4 text-[13px] font-medium uppercase tracking-[.08em] no-underline"
                            >
                                {group.title}
                            </a>
                        ))}
                    </nav>

                    <section
                        className={`sec grid grid-cols-1 ${
                            orderAvailable ? "desk:grid-cols-[minmax(0,1fr)_360px]" : ""
                        } items-start gap-[clamp(24px,4vw,64px)] pt-16 pb-[140px]`}
                    >
                        <div className="grid min-w-0 gap-[72px]">
                            {groups.map((group) => (
                                <div key={group._id} id={`kat-${group._id}`} className="scroll-mt-[170px]">
                                    <h2 className="m-0 mb-1.5 border-b border-forest pb-3.5 font-caslon text-[clamp(26px,2.6vw,36px)] font-normal tracking-[-.01em]">
                                        {group.title}
                                    </h2>
                                    <div className="xwide:grid xwide:grid-cols-2 xwide:gap-x-12">
                                        {group.items.map((item) => {
                                            const count = countFor(item._id);
                                            return (
                                                <div
                                                    key={item._id}
                                                    className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-6 gap-y-1.5 border-b border-forest/10 py-[18px]"
                                                >
                                                    <div className="min-w-0">
                                                        <div className="text-lg">
                                                            {item.title}
                                                            {item.vegetarian && <VegetarianMark />}
                                                        </div>
                                                        {item.description && (
                                                            <div className="mt-0.5 text-sm leading-normal text-forest/70">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                        {orderAvailable && item.sideDish && (
                                                            <SideDishSelect
                                                                value={sides[item._id] || "Pommes"}
                                                                onChange={(value) =>
                                                                    setSides((s) => ({ ...s, [item._id]: value }))
                                                                }
                                                                label={`Beilage für ${item.title}`}
                                                                className="mt-2.5"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2.5">
                                                        <span className="text-[17px] tabular-nums">
                                                            {euro(item.prices[0].amount)}
                                                        </span>
                                                        {orderAvailable && (
                                                            <button
                                                                type="button"
                                                                onClick={() => add(item)}
                                                                disabled={count >= MAX_QUANTITY}
                                                                aria-label={`${item.title} in den Warenkorb${
                                                                    count ? ` (bereits ${count})` : ""
                                                                }`}
                                                                className={`flex h-11 min-w-[44px] items-center gap-2 border border-forest px-3.5 text-sm font-semibold disabled:opacity-40 ${
                                                                    count ? "bg-forest text-creme" : "bg-transparent text-forest"
                                                                }`}
                                                            >
                                                                <span className="text-xl leading-none">+</span>
                                                                {count > 0 && <span>{count}</span>}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {orderAvailable && (
                            <aside
                                aria-label="Warenkorb"
                                className="hidden desk:block sticky top-[200px] bg-leinen p-7"
                            >
                                <div className="eyebrow mb-4">Warenkorb</div>
                                {cart.length === 0 && (
                                    <div className="pt-2 pb-4 text-[15px] leading-[1.6] text-forest/70">
                                        Noch leer. Wählen Sie Gerichte aus der Karte.
                                    </div>
                                )}
                                {cart.map((ci) => (
                                    <div
                                        key={ci.id + ci.beilage}
                                        className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 border-b border-forest/10 py-3 text-[15px]"
                                    >
                                        <div className="min-w-0">
                                            <div>{ci.title}</div>
                                            {ci.beilage && (
                                                <div className="text-sm text-forest/70">mit {ci.beilage}</div>
                                            )}
                                        </div>
                                        <div className="tabular-nums">{euro(ci.price * ci.quantity)}</div>
                                        <div className="col-span-full mt-1">
                                            <QuantityStepper
                                                max={MAX_QUANTITY}
                                                quantity={ci.quantity}
                                                title={ci.title}
                                                onDecrease={() => decreaseQuantity(ci.id, ci.beilage)}
                                                onIncrease={() => increaseQuantity(ci.id, ci.beilage)}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="flex justify-between pt-[18px] pb-5 text-[17px]">
                                    <span>Gesamt</span>
                                    <span className="tabular-nums">{euro(total)}</span>
                                </div>
                                <button
                                    type="button"
                                    disabled={cart.length === 0}
                                    onClick={() => navigate("/checkout")}
                                    className="btn btn-primary w-full py-[18px]"
                                >
                                    Zur Kasse
                                </button>
                            </aside>
                        )}
                    </section>

                    {orderAvailable && cart.length > 0 && (
                        <div className="desk:hidden fixed inset-x-0 bottom-0 z-[15] border-t border-forest/10 bg-creme/95 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] backdrop-blur">
                            <button
                                type="button"
                                onClick={() => navigate("/checkout")}
                                className="btn btn-primary w-full justify-between px-5 py-[18px]"
                            >
                                <span>Zur Kasse · {itemCount} Artikel</span>
                                <span className="tabular-nums">{euro(total)}</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Bestellen;
